import { Icon, Text, cn } from '@eluve/components';
import type {
  Cadence,
  ControlBandSpec,
  PlanActivity,
} from '@eluve/plan-shapes';

import { ActivityList } from './ActivityList';
import { MarkdownProse } from './MarkdownProse';
import type { Accent } from './accents';
import { formatCadence, formatTargetValue } from './format';
import { Eyebrow, IconTile, MonoChip, Panel } from './panel';

// Schematic axis for a band: pad the in-range segment by one band-width on
// each side and round the ends to whole numbers. A template has no data, so
// the geometry is illustrative — the labels are exact.
function bandAxis(band: ControlBandSpec): {
  lo: number;
  hi: number;
  fillFrom: number;
  fillTo: number;
} {
  const { low, high } = band;
  if (low !== undefined && high !== undefined) {
    const span = Math.max(high - low, 1e-6);
    return {
      lo: Math.max(0, Math.floor(low - span)),
      hi: Math.ceil(high + span),
      fillFrom: low,
      fillTo: high,
    };
  }
  if (high !== undefined) {
    return { lo: 0, hi: Math.ceil(high * 1.5) || 1, fillFrom: 0, fillTo: high };
  }
  if (low !== undefined) {
    return {
      lo: 0,
      hi: Math.ceil(low * 1.5) || 1,
      fillFrom: low,
      fillTo: Math.ceil(low * 1.5) || 1,
    };
  }
  return { lo: 0, hi: 1, fillFrom: 0, fillTo: 1 };
}

// ControlBand — the continuous shape's render primitive: each monitored metric
// as a labeled in-range band on a schematic axis, plus the monitoring cadence
// and review rule. Template skeleton only (no patient data).
export function ControlBand({
  bands,
  cadence,
  reviewRule,
  activities,
  accent,
}: {
  bands: ControlBandSpec[];
  cadence?: Cadence;
  reviewRule?: string;
  activities?: PlanActivity[];
  accent: Accent;
}) {
  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {bands.map((band, i) => {
          const axis = bandAxis(band);
          const pct = (v: number) =>
            ((v - axis.lo) / (axis.hi - axis.lo)) * 100;
          const left = pct(axis.fillFrom);
          const width = Math.max(pct(axis.fillTo) - left, 2);
          const center = Math.min(Math.max(left + width / 2, 12), 88);
          return (
            <li key={i}>
              <Panel className="px-4 py-3.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <Text inline>{band.measure.text}</Text>
                  <MonoChip className={accent.tile}>
                    {formatTargetValue(band)}
                  </MonoChip>
                </div>
                {/* The band: target range highlighted on a schematic axis. */}
                <div className="relative mt-3 h-2.5 rounded-full bg-muted">
                  <div
                    className={cn(
                      'absolute inset-y-0 rounded-full',
                      accent.fill,
                    )}
                    style={{ left: `${left}%`, width: `${width}%` }}
                  />
                </div>
                <div className="relative mt-1 flex justify-between font-mono text-[10px] text-muted-foreground">
                  <span>{axis.lo}</span>
                  <span
                    className={cn(
                      'absolute -translate-x-1/2 whitespace-nowrap',
                      accent.text,
                    )}
                    style={{ left: `${center}%` }}
                  >
                    target band
                  </span>
                  <span>{axis.hi}</span>
                </div>
              </Panel>
            </li>
          );
        })}
      </ul>

      {(cadence || reviewRule) && (
        <div className="flex flex-col gap-3 sm:flex-row">
          {cadence && (
            <Panel className="flex flex-1 items-start gap-3 px-4 py-3.5">
              <IconTile className={accent.tile}>
                <Icon name="CalendarClock" size="auto" />
              </IconTile>
              <div className="min-w-0">
                <Eyebrow>Monitoring cadence</Eyebrow>
                <p className="mt-1 font-mono text-sm font-medium text-foreground">
                  {formatCadence(cadence)}
                </p>
              </div>
            </Panel>
          )}
          {reviewRule && (
            <Panel className="flex flex-[1.7] items-start gap-3 px-4 py-3.5">
              <IconTile className={accent.tile}>
                <Icon name="BellRing" size="auto" />
              </IconTile>
              <div className="min-w-0">
                <Eyebrow>Review rule</Eyebrow>
                <MarkdownProse
                  content={reviewRule}
                  className="mt-1 text-sm text-foreground"
                />
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* Standing activities the patient performs on an ongoing basis. */}
      {activities && activities.length > 0 && (
        <Panel className="px-4 py-3.5">
          <Eyebrow>Standing activities</Eyebrow>
          <ActivityList
            activities={activities}
            accent={accent}
            className="mt-2"
          />
        </Panel>
      )}
    </div>
  );
}
